import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { globalTheme } from '../../styles/global-theme';
import { Flex, Icon, Text, Tooltip } from '../ui';
import Link from 'next/link';

export interface FileChipProps {
  id: string;
  name: string;
  urls: string;
  removeMediaItem?: (id: string) => void;
  handleEditFileName?: (id: string, name: string) => void;
  disabled?: boolean;
  mode?: 'add' | 'edit';
  fileType?: string;
  setSelectedMediaItems?: () => void;
  deleteEnabled?: boolean;
  selected?: boolean;
  isMobile?: boolean;
  bookingCell?: boolean;
}

const Wrapper = styled(Flex)<{
  disabled?: boolean;
  selected?: boolean;
  isMobile?: boolean;
}>`
  position: relative;
  max-width: ${({ isMobile }) => (!isMobile ? '300px' : 'unset')};
  background-color: ${(props) =>
    props.selected ? globalTheme.colors.blue : globalTheme.colors.lightBlue};
  padding: 6px ${globalTheme.space[1]};
  border-radius: 20px;
  border: 1px solid ${globalTheme.colors.blue};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
`;

const IconWrap = styled.div`
  cursor: pointer;
`;

const FileName = styled(Text)<{ selected?: boolean }>`
  color: ${(props) =>
    props.selected ? globalTheme.colors.white : globalTheme.colors.blue};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  cursor: pointer;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const RemoveIcon = styled.div<{ isEditable?: boolean }>`
  cursor: pointer;

  & div {
    height: 13px;
    width: 13px;
  }
`;

const NewWindowIcon = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  & div {
    display: flex;
    align-items: center;
  }
`;

const ConfirmIconWrap = styled.div`
  right: 10px;
  z-index: 9;
`;

const FileLink = styled(Link)<{ $isDisabled?: boolean; $isSelected?: boolean }>`
  cursor: ${(props) => (props.$isDisabled ? 'not-allowed' : 'pointer')};
  text-decoration: ${(props) => (props.$isDisabled ? 'none' : 'underline')};
  color: ${(props) =>
    props.$isSelected ? globalTheme.colors.white : globalTheme.colors.blue};
`;

// Todo disabled as this was changed with the media implementation
// const FileName = styled(Text)<{ isDisabled?: boolean; isSelected?: boolean }>`
//   cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
//   text-decoration: ${(props) => (props.isDisabled ? 'none' : 'underline')};
//   color: ${(props) =>
//     props.isSelected ? globalTheme.colors.white : globalTheme.colors.blue}}
// `;

export const FileChip: FC<FileChipProps> = ({
  id,
  name,
  urls,
  removeMediaItem,
  handleEditFileName,
  disabled,
  mode = 'edit',
  fileType = 'PDF',
  setSelectedMediaItems,
  selected,
  deleteEnabled,
  isMobile,
  bookingCell,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isNameEditing, setIsNameEditing] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>(name);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputRef.current) {
      inputRef.current.blur();
      setIsNameEditing(false);
    }
  };

  useEffect(() => {
    if (isNameEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isNameEditing]);

  return (
    <div
      style={{ display: 'flex', alignItems: 'center' }}
      onClick={setSelectedMediaItems}
    >
      <Wrapper
        key={`${id}_name`}
        gap={0}
        alignItems='center'
        justifyContent='space-between'
        disabled={disabled}
        selected={selected}
        isMobile={isMobile}
      >
        {!isNameEditing && (
          <Flex gap={0} alignItems='center'>
            <FileName
              selected={selected}
              fontSize={bookingCell ? 'bookingMobile' : 'default'}
            >
              {name}
            </FileName>
          </Flex>
        )}
        {isNameEditing && (
          <>
            <input
              onKeyDown={handleKeyPress}
              onBlur={() => {
                setIsNameEditing(false);
                handleEditFileName?.(id, fileName);
              }}
              ref={inputRef}
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              style={{ maxWidth: '150px' }}
            />
          </>
        )}
        <IconWrapper>
          {!disabled &&
            handleEditFileName &&
            mode === 'edit' &&
            !isNameEditing && (
              <Tooltip label='Edit File Name'>
                <IconWrap onClick={() => setIsNameEditing(true)}>
                  <Icon
                    component='EditIcon'
                    aria-label={`Remove ${name}`}
                    size={1}
                    color={!selected ? 'blue' : 'white'}
                  />
                </IconWrap>
              </Tooltip>
            )}
          {!disabled && isNameEditing && (
            <Tooltip label='Confirm filename'>
              <ConfirmIconWrap>
                <IconWrap
                  onClick={() => {
                    setIsNameEditing(false);
                    handleEditFileName?.(id, fileName);
                  }}
                >
                  <Icon
                    component='CheckIcon'
                    aria-label={`Confirm icon rename ${name}`}
                    size={2}
                    color={!selected ? 'blue' : 'white'}
                  />
                </IconWrap>
              </ConfirmIconWrap>
            </Tooltip>
          )}
          <FileLink
            href={urls}
            $isDisabled={mode === 'add'}
            target='_blank'
            rel='noopener noreferrer'
            $isSelected={selected}
          >
            <NewWindowIcon>
              <Tooltip label='Open new window'>
                <IconWrap
                  onClick={() => {
                    setIsNameEditing(false);
                    handleEditFileName?.(id, fileName);
                  }}
                >
                  <Icon
                    component='NewWindow'
                    aria-label={`New window ${name}`}
                    size={1}
                    color={!selected ? 'blue' : 'white'}
                  />
                </IconWrap>
              </Tooltip>
            </NewWindowIcon>
          </FileLink>
          {!disabled && removeMediaItem && !isNameEditing && (
            <Tooltip label='Remove media'>
              <RemoveIcon
                isEditable={
                  handleEditFileName && mode === 'edit' ? true : false
                }
                onClick={() => removeMediaItem(id)}
              >
                <Icon
                  component='CancelIcon'
                  aria-label={`Remove ${name}`}
                  size={1}
                  color={!selected ? 'blue' : 'white'}
                />
              </RemoveIcon>
            </Tooltip>
          )}
        </IconWrapper>
      </Wrapper>
    </div>
  );
};
